
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI } from '../../services/api';
import { 
  BookOpen, 
  TrendingUp, 
  Target, 
  Zap,
  Calendar,
  Trophy,
  ArrowRight,
  Play,
  BarChart3,
  Brain,
  Star
} from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dashStats, setDashStats] = useState({
    testsCompleted: 0,
    testsThisWeek: 0,
    averageScore: 0,
    studyStreak: 0,
    rank: 0,
  });

  useEffect(() => {
    analyticsAPI.getDashboardStats()
      .then((res) => setDashStats(res.data.data))
      .catch(() => {}); // silently fail, keep zeros
  }, []);

  const handleQuickAction = (actionType: string) => {
    switch (actionType) {
      case 'test':
        navigate('/tests');
        break;
      case 'practice':
        navigate('/practice');
        break;
      case 'analytics':
        navigate('/analytics');
        break;
      case 'study-plan':
        navigate('/study-plan');
        break;
      default:
        break;
    }
  };

  const stats = [
    {
      label: 'Tests Completed',
      value: String(dashStats.testsCompleted),
      change: `+${dashStats.testsThisWeek} this week`,
      changeType: 'positive',
      icon: BookOpen,
      color: 'primary'
    },
    {
      label: 'Average Score',
      value: `${dashStats.averageScore}%`,
      change: dashStats.averageScore >= 60 ? 'Good progress' : 'Keep practicing',
      changeType: dashStats.averageScore >= 60 ? 'positive' : 'neutral',
      icon: TrendingUp,
      color: 'success'
    },
    {
      label: 'Study Streak',
      value: `${dashStats.studyStreak} days`,
      change: dashStats.studyStreak > 0 ? 'Keep it up!' : 'Start today!',
      changeType: 'neutral',
      icon: Zap,
      color: 'warning'
    },
    {
      label: 'Rank',
      value: dashStats.rank > 0 ? `#${dashStats.rank}` : '—',
      change: 'Among all students',
      changeType: 'positive',
      icon: Trophy,
      color: 'accent'
    }
  ];

  const quickActions = [
    {
      title: 'Take Mock Test',
      description: 'Start a full-length JEE mock test',
      icon: Play,
      color: 'primary',
      action: 'Start Test',
      actionType: 'test'
    },
    {
      title: 'Smart Practice',
      description: 'AI-recommended questions based on your weak areas',
      icon: Brain,
      color: 'success',
      action: 'Practice Now',
      actionType: 'practice'
    },
    {
      title: 'View Analytics',
      description: 'Detailed performance insights and recommendations',
      icon: BarChart3,
      color: 'secondary',
      action: 'View Report',
      actionType: 'analytics'
    },
    {
      title: 'Study Plan',
      description: 'Follow your personalized daily study schedule',
      icon: Calendar,
      color: 'accent',
      action: 'Open Plan',
      actionType: 'study-plan'
    }
  ];

  const recentActivity = [
    {
      type: 'test',
      title: 'JEE Main Mock Test #15',
      score: '82%',
      time: '2 hours ago',
      status: 'completed'
    },
    {
      type: 'practice',
      title: 'Calculus - Limits Practice',
      score: '90%',
      time: '1 day ago',
      status: 'completed'
    },
    {
      type: 'badge',
      title: 'Earned "Speed Demon" badge',
      score: null,
      time: '2 days ago',
      status: 'achievement'
    }
  ];



  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
              Welcome back, {user?.name}! 👋
            </h1>
            <p className="text-secondary-600 text-base md:text-lg">
              Ready to ace your JEE preparation today?
            </p>
          </div>
          <div className="flex md:hidden items-center justify-between p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl">
            <div>
              <div className="text-xs text-secondary-500">Today's Goal</div>
              <div className="text-base font-semibold text-secondary-900">2 Tests</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <Target className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-secondary-500">Today's Goal</div>
              <div className="text-lg font-semibold text-secondary-900">2 Tests</div>
            </div>
            <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center">
              <Target className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const colorClasses = {
            primary: 'from-primary-500 to-primary-600',
            success: 'from-success-500 to-success-600',
            warning: 'from-warning-500 to-warning-600',
            accent: 'from-accent-500 to-accent-600',
            secondary: 'from-secondary-500 to-secondary-600'
          };
          
          return (
            <div key={index} className="stats-card group">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${colorClasses[stat.color as keyof typeof colorClasses]} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className={`text-[10px] md:text-xs font-medium px-2 py-1 rounded-full ${
                  stat.changeType === 'positive' ? 'bg-success-100 text-success-700' :
                  stat.changeType === 'negative' ? 'bg-danger-100 text-danger-700' :
                  'bg-secondary-100 text-secondary-700'
                }`}>
                  {stat.change}
                </div>
              </div>
              <div className="stats-value mb-1">{stat.value}</div>
              <div className="stats-label">{stat.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-secondary-900">Quick Actions</h2>
            <button className="btn-ghost btn-sm text-xs md:text-sm">
              View All
              <ArrowRight className="ml-2 h-3 w-3 md:h-4 md:w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              const colorClasses = {
                primary: 'from-primary-500 to-primary-600',
                success: 'from-success-500 to-success-600',
                warning: 'from-warning-500 to-warning-600',
                accent: 'from-accent-500 to-accent-600',
                secondary: 'from-secondary-500 to-secondary-600'
              };
              
              const buttonClasses = {
                primary: 'btn-primary',
                success: 'btn-success',
                warning: 'btn-warning',
                accent: 'btn-primary',
                secondary: 'btn-secondary'
              };
              
              return (
                <div key={index} className="card-interactive p-4 md:p-6">
                  <div className="flex items-start justify-between mb-3 md:mb-4">
                    <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${colorClasses[action.color as keyof typeof colorClasses]} rounded-xl flex items-center justify-center`}>
                      <Icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <ArrowRight className="h-4 w-4 md:h-5 md:w-5 text-secondary-400 group-hover:text-secondary-600 transition-colors" />
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-secondary-900 mb-2">
                    {action.title}
                  </h3>
                  <p className="text-secondary-600 mb-3 md:mb-4 text-xs md:text-sm leading-relaxed">
                    {action.description}
                  </p>
                  <button 
                    onClick={() => handleQuickAction(action.actionType)}
                    className={`${buttonClasses[action.color as keyof typeof buttonClasses]} btn-sm w-full touch-target`}
                  >
                    {action.action}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg md:text-xl font-semibold text-secondary-900">Recent Activity</h3>
            </div>
            <div className="card-content">
              <div className="space-y-3 md:space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 md:p-4 bg-secondary-50 rounded-xl hover:bg-secondary-100 transition-colors">
                    <div className="flex items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'test' ? 'bg-primary-100 text-primary-600' :
                        activity.type === 'practice' ? 'bg-success-100 text-success-600' :
                        'bg-warning-100 text-warning-600'
                      }`}>
                        {activity.type === 'test' ? <BookOpen className="h-4 w-4 md:h-5 md:w-5" /> :
                         activity.type === 'practice' ? <Brain className="h-4 w-4 md:h-5 md:w-5" /> :
                         <Star className="h-4 w-4 md:h-5 md:w-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-secondary-900 text-sm md:text-base truncate">{activity.title}</div>
                        <div className="text-xs md:text-sm text-secondary-500">{activity.time}</div>
                      </div>
                    </div>
                    {activity.score && (
                      <div className="text-right ml-2 flex-shrink-0">
                        <div className="font-semibold text-secondary-900 text-sm md:text-base">{activity.score}</div>
                        <div className="text-[10px] md:text-xs text-secondary-500">Score</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 md:space-y-6">

          {/* Study Streak */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-base md:text-lg font-semibold text-secondary-900 flex items-center gap-2">
                <Zap className="h-5 w-5 text-warning-500" /> Study Streak
              </h3>
            </div>
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-4xl font-bold text-secondary-900">{dashStats.studyStreak}</div>
                  <div className="text-sm text-secondary-500 mt-1">days in a row</div>
                </div>
                <div className="w-16 h-16 bg-gradient-to-r from-warning-400 to-warning-500 rounded-2xl flex items-center justify-center">
                  <Zap className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className={`mt-3 text-xs font-medium px-3 py-1.5 rounded-full inline-block ${
                dashStats.studyStreak >= 7 ? 'bg-warning-100 text-warning-700' : 'bg-secondary-100 text-secondary-600'
              }`}>
                {dashStats.studyStreak >= 30 ? '🔥 On fire!' : dashStats.studyStreak >= 7 ? '⚡ Great streak!' : dashStats.studyStreak > 0 ? 'Keep going!' : 'Start today!'}
              </div>
            </div>
          </div>

          {/* Rank */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-base md:text-lg font-semibold text-secondary-900 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-accent-500" /> Your Rank
              </h3>
            </div>
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-4xl font-bold text-secondary-900">
                    {dashStats.rank > 0 ? `#${dashStats.rank}` : '—'}
                  </div>
                  <div className="text-sm text-secondary-500 mt-1">among all students</div>
                </div>
                <div className="w-16 h-16 bg-gradient-to-r from-accent-400 to-accent-500 rounded-2xl flex items-center justify-center">
                  <Trophy className="h-8 w-8 text-white" />
                </div>
              </div>
              <button
                onClick={() => navigate('/analytics')}
                className="mt-3 w-full btn-ghost btn-sm text-xs"
              >
                View full leaderboard <ArrowRight className="ml-1 h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Weekly Goals */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-base md:text-lg font-semibold text-secondary-900 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary-500" /> Weekly Goals
              </h3>
            </div>
            <div className="card-content space-y-3">
              {/* Tests goal */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-secondary-600">Tests this week</span>
                  <span className="font-semibold text-secondary-900">{dashStats.testsThisWeek} / 5</span>
                </div>
                <div className="w-full bg-secondary-100 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (dashStats.testsThisWeek / 5) * 100)}%` }}
                  />
                </div>
              </div>
              {/* Score goal */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-secondary-600">Avg score target</span>
                  <span className="font-semibold text-secondary-900">{dashStats.averageScore}% / 75%</span>
                </div>
                <div className="w-full bg-secondary-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${dashStats.averageScore >= 75 ? 'bg-success-500' : 'bg-warning-500'}`}
                    style={{ width: `${Math.min(100, (dashStats.averageScore / 75) * 100)}%` }}
                  />
                </div>
              </div>
              {/* Streak goal */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-secondary-600">Streak goal</span>
                  <span className="font-semibold text-secondary-900">{dashStats.studyStreak} / 7 days</span>
                </div>
                <div className="w-full bg-secondary-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${dashStats.studyStreak >= 7 ? 'bg-success-500' : 'bg-accent-500'}`}
                    style={{ width: `${Math.min(100, (dashStats.studyStreak / 7) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}